import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Send, RefreshCw, Search, MessageCircle, Megaphone, RotateCw } from "lucide-react";
import { Link } from "react-router-dom";

const MEDIA_TYPES = ["image", "sticker", "video", "audio", "document"];

function MediaBubble({ m }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let revoked = false; let objectUrl = null;
    api.get(`/whatsapp/media/${m.id}`, { responseType: "blob" })
      .then(({ data }) => { if (revoked) return; objectUrl = URL.createObjectURL(data); setUrl(objectUrl); })
      .catch(() => setFailed(true));
    return () => { revoked = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [m.id]);
  if (failed) return <p className="text-sm text-muted-foreground">[{m.type} could not be loaded. WhatsApp keeps media for about 30 days.]</p>;
  if (!url) return <p className="text-sm text-muted-foreground">Loading {m.type}…</p>;
  if (m.type === "image" || m.type === "sticker") return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={m.type} className="max-h-72 rounded-lg" /></a>;
  if (m.type === "video") return <video src={url} controls className="max-h-72 rounded-lg" />;
  if (m.type === "audio") return <audio src={url} controls />;
  return <a href={url} download={m.text || "file"} className="text-sm underline">Download {m.text || "document"}</a>;
}

export default function Whatsapp() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  const autoSelected = useRef(false);
  const loadConversations = async () => {
    try {
      const { data } = await api.get("/whatsapp/conversations");
      setConversations(data);
      // open the first chat only once, on first load; never override the chat you picked
      if (!autoSelected.current && data[0]) { autoSelected.current = true; selectConversation(data[0]); }
    }
    catch { toast.error("Could not load WhatsApp inbox"); }
  };
  const selectConversation = async (item) => {
    setSelected(item);
    try { const { data } = await api.get(`/whatsapp/conversations/${item.customer_id}`); setMessages(data.messages || []); }
    catch { toast.error("Could not load conversation"); }
  };
  useEffect(() => { loadConversations(); const t = setInterval(loadConversations, 10000); return () => clearInterval(t); }, []);
  useEffect(() => { if (!selected) return; const t = setInterval(() => selectConversation(selected), 5000); return () => clearInterval(t); }, [selected?.customer_id]);

  const filtered = useMemo(() => conversations.filter(x => {
    const q = search.toLowerCase();
    return !q || x.customer?.name?.toLowerCase().includes(q) || x.customer?.whatsapp?.includes(q);
  }), [conversations, search]);

  const resend = async (m) => {
    setResendingId(m.id);
    try {
      await api.post(`/whatsapp/messages/${m.id}/resend`);
      toast.success("Message resent");
      await selectConversation(selected); await loadConversations();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resend message"); }
    finally { setResendingId(null); }
  };

  const failureReason = (m) => {
    const err = (m.errors || [])[0];
    if (!err) return null;
    return err.title || err.message || err.error_data?.details || "Delivery failed";
  };

  const send = async () => {
    if (!body.trim() || !selected) return;
    setLoading(true);
    try {
      await api.post(`/whatsapp/conversations/${selected.customer_id}/messages`, { body: body.trim() });
      setBody(""); await selectConversation(selected); await loadConversations();
    } catch (e) { toast.error(e?.response?.data?.detail || "Message could not be sent"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-5 h-[calc(100vh-8rem)] min-h-[620px]">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div><p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Customer Service</p><h1 className="font-serif-elegant text-5xl mt-1">WhatsApp Inbox</h1></div>
        <div className="flex gap-2"><Link to="/whatsapp/broadcast"><Button variant="outline"><Megaphone size={14} className="mr-2"/>Broadcast</Button></Link><Button variant="outline" onClick={loadConversations}><RefreshCw size={14}/></Button></div>
      </div>
      <Card className="h-full overflow-hidden bg-white border-border grid grid-cols-1 md:grid-cols-[320px_1fr]">
        <div className="border-r hairline flex flex-col min-h-0">
          <div className="p-3 border-b hairline"><div className="relative"><Search size={15} className="absolute left-3 top-2.5 text-muted-foreground"/><Input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search customers" className="pl-9"/></div></div>
          <div className="overflow-y-auto flex-1">
            {filtered.map(item => <button key={item.customer_id} onClick={()=>selectConversation(item)} className={`w-full text-left p-4 border-b hairline hover:bg-accent/50 ${selected?.customer_id===item.customer_id?"bg-accent/70":""}`}>
              <div className="flex justify-between gap-2"><span className="font-semibold truncate">{item.customer?.name}</span>{item.unread>0 && <Badge>{item.unread}</Badge>}</div>
              <p className="text-xs text-muted-foreground mt-1 truncate">{item.last_message?.text || (MEDIA_TYPES.includes(item.last_message?.type) ? `📎 ${item.last_message?.type}` : `[${item.last_message?.type}]`)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">{item.last_message?.created_at?.slice(0,16).replace("T"," ")}</p>
            </button>)}
            {!filtered.length && <div className="p-8 text-center text-sm text-muted-foreground">No WhatsApp conversations yet.</div>}
          </div>
        </div>
        <div className="min-h-0 flex flex-col">
          {!selected ? <div className="flex-1 flex items-center justify-center text-muted-foreground"><div className="text-center"><MessageCircle className="mx-auto mb-3" size={32}/><p>Select a conversation</p></div></div> : <>
            <div className="p-4 border-b hairline"><p className="font-semibold">{selected.customer?.name}</p><p className="text-xs text-muted-foreground">{selected.customer?.whatsapp || selected.customer?.phone}</p></div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-[#fbfaf7]">
              {messages.map(m => <div key={m.id} className={`flex ${m.direction==='outbound'?"justify-end":"justify-start"}`}><div className={`max-w-[75%] rounded-xl px-4 py-2.5 shadow-sm ${m.direction==='outbound'?"bg-primary text-white":"bg-white border hairline"}`}>
                {MEDIA_TYPES.includes(m.type) && m.raw?.[m.type]?.id && <MediaBubble m={m} />}
                {(m.text || !MEDIA_TYPES.includes(m.type)) && <p className="text-sm whitespace-pre-wrap">{m.text || `[${m.type}]`}</p>}
                <p className={`text-[10px] mt-1 ${m.direction==='outbound'?"text-white/70":"text-muted-foreground"}`}>{m.created_at?.slice(11,16)} {m.direction==='outbound' ? `· ${m.status}` : ""}</p>
                {m.direction==='outbound' && m.status==='failed' && (
                  <div className="mt-1.5 pt-1.5 border-t border-white/20">
                    {failureReason(m) && <p className="text-[10px] text-white/90">{failureReason(m)}</p>}
                    <button onClick={()=>resend(m)} disabled={resendingId===m.id}
                            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium underline decoration-white/50 hover:decoration-white disabled:opacity-60">
                      <RotateCw size={11} className={resendingId===m.id?"animate-spin":""}/>
                      {resendingId===m.id ? "Resending…" : "Resend"}
                    </button>
                  </div>
                )}
              </div></div>)}
            </div>
            <div className="p-3 border-t hairline flex gap-2"><Input value={body} onChange={e=>setBody(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();send();}}} placeholder="Reply to customer…"/><Button onClick={send} disabled={loading||!body.trim()}><Send size={14}/></Button></div>
            <p className="px-4 pb-3 text-[10px] text-muted-foreground">Replies are free-form only while the 24-hour customer-service window is open. Outside it, use an approved Meta template.</p>
          </>}
        </div>
      </Card>
    </div>
  );
}
